import styled from "@emotion/styled";
import { useState, type ReactNode } from "react";

import {
  ContextMenu,
  ContextMenuIconButton,
  FONT_WEIGHT_SEMI_BOLD,
  Icon,
  PrefixChip,
  Skeleton,
} from "@skilitics/designer-ui-components";

import { useWorkspaceInfo, useWorkspaceListQuery } from "../../auth/workspaces.ts";
import { WorkspaceAvatar } from "../../ui/WorkspaceAvatar";
import { WorkspaceMenuItems } from "../../ui/WorkspaceMenu";
import { SIDEBAR_ITEM_RADIUS, SIDEBAR_WIDTH, sidebarItemCss } from "./sidebarStyles";

const iconSize = 14;
const avatarSize = 26;

const Container = styled.div`
  margin-left: -4px;
  ${ContextMenuIconButton} {
    width: 100%;
    height: auto;
    padding: 0;
    justify-content: flex-start;
    border-radius: ${SIDEBAR_ITEM_RADIUS}px;
  }
`;

const Trigger = styled.div`
  ${sidebarItemCss}
  justify-content: space-between;
  width: 100%;
  min-width: 0;
`;

const StyledWorkspaceAvatar = styled(WorkspaceAvatar)`
  width: ${avatarSize}px;
  height: ${avatarSize}px;
  flex: 0 0 auto;
  font-size: 12px;
`;

const TriggerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const WorkspaceName = styled.span`
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
`;

const PlanChip = styled(PrefixChip)`
  flex: 0 0 auto;
  margin-right: 0;
`;

export const WorkspaceSwitcher = () => {
  const selected = useWorkspaceInfo();
  const { isLoading } = useWorkspaceListQuery();

  // We currently only have personal billing, so this reflects the current user's plan.
  const isPremium = false; // useCurrentPlanKey() === "interact_pro";

  const [newOrgDialog, setNewOrgDialog] = useState<ReactNode>(null);

  return (
    <Container>
      <ContextMenu
        slotProps={{
          paper: {
            style: { width: SIDEBAR_WIDTH },
          },
        }}
        IconComponent={
          <Trigger data-test="workspace-switcher">
            <TriggerContent>
              {isLoading ? (
                <>
                  <Skeleton variant="circular" width={avatarSize} height={avatarSize} animation="wave" />
                  <Skeleton variant="text" width="60%" animation="wave" />
                </>
              ) : (
                <>
                  <StyledWorkspaceAvatar name={selected?.name ?? "-"} seed={selected?.id ?? "personal"} />
                  <WorkspaceName>{selected?.name ?? "-"}</WorkspaceName>
                </>
              )}
            </TriggerContent>
            <Icon icon="angle-down" size={iconSize} />
            {isPremium && <PlanChip type="primary" label="Pro" />}
          </Trigger>
        }
        data-test="workspace-switcher-button"
      >
        <WorkspaceMenuItems setNewOrgDialog={setNewOrgDialog} />
      </ContextMenu>
      {newOrgDialog}
    </Container>
  );
};
